import { useCallback } from 'react';
import posthog from 'posthog-js';
import { isPostHogEnabled } from '../config/posthog-config';
import type {
  AnalyticsEvent,
  AnalyticsEventProperties,
} from '../types/analytics.types';

/**
 * Hook for tracking analytics events
 */
export const useAnalytics = () => {
  /**
   * Track an event
   */
  const trackEvent = useCallback(
    (event: AnalyticsEvent, properties?: AnalyticsEventProperties) => {
      if (!isPostHogEnabled()) {
        if (import.meta.env.DEV) {
          console.log('[Analytics]', event, properties);
        }
        return;
      }

      try {
        posthog.capture(event, properties);
      } catch (error) {
        console.error('[Analytics] Failed to track event:', event, error);
      }
    },
    []
  );

  /**
   * Identify user (for authenticated users in the future)
   */
  const identifyUser = useCallback(
    (userId: string, properties?: AnalyticsEventProperties) => {
      if (!isPostHogEnabled()) return;

      try {
        posthog.identify(userId, properties);
      } catch (error) {
        console.error('[Analytics] Failed to identify user:', error);
      }
    },
    []
  );

  /**
   * Reset user identity (on logout)
   */
  const resetUser = useCallback(() => {
    if (!isPostHogEnabled()) return;

    try {
      posthog.reset();
    } catch (error) {
      console.error('[Analytics] Failed to reset user:', error);
    }
  }, []);

  /**
   * Set user properties
   */
  const setUserProperties = useCallback(
    (properties: AnalyticsEventProperties) => {
      if (!isPostHogEnabled()) return;

      try {
        posthog.setPersonProperties(properties);
      } catch (error) {
        console.error('[Analytics] Failed to set user properties:', error);
      }
    },
    []
  );

  /**
   * Track page view
   */
  const trackPageView = useCallback((pageName?: string) => {
    if (!isPostHogEnabled()) {
      if (import.meta.env.DEV) {
        console.log(
          '[Analytics] Page view:',
          pageName || window.location.pathname
        );
      }
      return;
    }

    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        page_name: pageName,
      });
    } catch (error) {
      console.error('[Analytics] Failed to track page view:', error);
    }
  }, []);

  return {
    trackEvent,
    identifyUser,
    resetUser,
    setUserProperties,
    trackPageView,
  };
};
